import { useEffect, useMemo, useState } from "react";
import { RotateCw, Download, ArrowRight, ArrowUpRight } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Sparkline } from "../../components/Sparkline";
import { useToast } from "../../components/Toast";
import { useCountUp, formatRu } from "../../hooks/useCountUp";
import {
  type LocalServer,
  type LogEvent,
  type EventColor,
} from "../../data";
import { Btn } from "../../components/Btn";
import {
  StatModal,
  NodeModal,
  EventModal,
  ExportModal,
  type ModalKind,
} from "../../components/dashboardModals";
import {
  getAdminKpis,
  listLocals,
  recentEvents,
} from "../../api/endpoints";
import { toLocalServer, toLogEvent } from "../../api/adapters";
import { useApiFallback } from "../../hooks/useApiFallback";
import type { AdminKpis } from "../../api/types";

// Реалистичный fallback на случай недоступности /admin/kpis: реальная пустая
// система должна показывать нули, а не "1284 / 127". Sparkline-ряды держим
// плоскими — никаких выдуманных трендов.
const EMPTY_KPIS: AdminKpis = {
  generation: 0,
  users_total: 0,
  active_users_24h: 0,
  active_users_14d_history: Array(14).fill(0),
  apps_published: 0,
  apps_revoked: 0,
  apps_draft: 0,
  apps_published_history: Array(14).fill(0),
  groups_total: 0,
  locals_total: 0,
  locals_online: 0,
  locals_stale: 0,
  locals_offline: 0,
  locals_online_history: Array(14).fill(0),
  avg_sync_minutes: 0,
  avg_sync_history: Array(14).fill(0),
  sync_target_minutes: 10,
};

export default function SummaryPage() {
  const [modal, setModal] = useState<ModalKind>(null);

  const localsQ = useQuery({
    queryKey: ["locals"],
    queryFn: listLocals,
  });
  const eventsQ = useQuery({
    queryKey: ["events", "recent"],
    queryFn: () => recentEvents(8),
  });
  const kpisQ = useQuery({
    queryKey: ["admin", "kpis"],
    queryFn: getAdminKpis,
    // Перезапрашиваем раз в минуту — числа на сводке должны быть свежими.
    refetchInterval: 60_000,
  });

  // Fallback на пустые массивы — НЕ показываем mock localServers / mockEvents,
  // чтобы при reload не мелькали несуществующие msk-01/ekb-01/события.
  const locals = useApiFallback<LocalServer[]>(
    localsQ.data?.map(toLocalServer),
    localsQ.isError,
    [],
  );
  const events = useApiFallback<LogEvent[]>(
    eventsQ.data?.map(toLogEvent),
    eventsQ.isError,
    [],
  );
  const kpis = useApiFallback<AdminKpis>(kpisQ.data, kpisQ.isError, EMPTY_KPIS);

  const loadingAny =
    localsQ.isLoading || eventsQ.isLoading || kpisQ.isLoading;

  return (
    <div className={loadingAny ? "opacity-90 transition-opacity" : "transition-opacity"}>
      <MainHeader
        generation={kpis.value.generation}
        onExport={() => setModal({ kind: "export" })}
        onRefresh={() => kpisQ.refetch()}
      />
      <StatGrid
        kpis={kpis.value}
        onOpen={(which) => setModal({ kind: "stat", which })}
      />
      <BottomGrid
        locals={locals.value}
        events={events.value}
        onNodeClick={(node) => setModal({ kind: "node", node })}
        onEventClick={(event) => setModal({ kind: "event", event })}
      />

      <StatModal modal={modal} onClose={() => setModal(null)} />
      <NodeModal modal={modal} onClose={() => setModal(null)} />
      <EventModal modal={modal} onClose={() => setModal(null)} />
      <ExportModal modal={modal} onClose={() => setModal(null)} />
    </div>
  );
}

function MainHeader({
  generation,
  onExport,
  onRefresh,
}: {
  generation: number;
  onExport: () => void;
  onRefresh: () => Promise<unknown> | void;
}) {
  const [spinning, setSpinning] = useState(false);
  const push = useToast();

  // Сбрасываем спиннер по тайм-ауту даже если refetch почему-то не разрезолвится —
  // лучше «застряло» 1.5с, чем вечный анимирующийся значок.
  useEffect(() => {
    if (!spinning) return;
    const t = setTimeout(() => setSpinning(false), 1500);
    return () => clearTimeout(t);
  }, [spinning]);

  const refresh = () => {
    if (spinning) return;
    setSpinning(true);
    Promise.resolve(onRefresh())
      .catch(() => undefined)
      .finally(() => {
        setSpinning(false);
        push({
          kind: "success",
          text: `Обновлено · поколение ${generation}`,
        });
      });
  };

  return (
    <div className="flex flex-col gap-3 pb-5 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
      <div className="min-w-0">
        <h1 className="text-[20px] font-semibold leading-tight tracking-[-0.01em] text-[var(--color-fg)] sm:text-[22px]">
          Состояние системы
        </h1>
        <div className="mt-1 text-[12.5px] text-[var(--color-fg-muted)]">
          Каталог поколения · поколение № <span className="font-mono">{generation}</span>
        </div>
      </div>
      <div className="flex gap-2">
        <Btn icon={RotateCw} onClick={refresh} iconClass={spinning ? "spin" : ""}>
          <span className="hidden sm:inline">{spinning ? "Обновление…" : "Обновить"}</span>
          <span className="sm:hidden">{spinning ? "…" : "Обновить"}</span>
        </Btn>
        <Btn icon={Download} onClick={onExport}>
          Экспорт
        </Btn>
      </div>
    </div>
  );
}

// ───────────────────── Stat cards ─────────────────────

function StatGrid({
  kpis,
  onOpen,
}: {
  kpis: AdminKpis;
  onOpen: (which: "locals" | "apps" | "users" | "delay") => void;
}) {
  const delayColor =
    kpis.avg_sync_minutes <= kpis.sync_target_minutes
      ? "var(--color-success)"
      : "var(--color-warning)";

  return (
    <div className="grid grid-cols-2 gap-3 md:grid-cols-2 xl:grid-cols-4">
      <StatCard
        label="Локальных серверов"
        target={kpis.locals_online}
        decimals={0}
        suffix={` / ${kpis.locals_total} онлайн`}
        sub="из них онлайн"
        spark={{ values: kpis.locals_online_history, color: "var(--color-brand-2)" }}
        onClick={() => onOpen("locals")}
      />
      <StatCard
        label="Опубликованных приложений"
        target={kpis.apps_published}
        decimals={0}
        accent
        sub={`${kpis.apps_revoked} отозвано · ${kpis.apps_draft} в черновиках`}
        spark={{ values: kpis.apps_published_history, color: "var(--color-brand-2)" }}
        onClick={() => onOpen("apps")}
      />
      <StatCard
        label="Активных пользователей"
        target={kpis.active_users_24h}
        decimals={0}
        sub={`за последние 24 ч · всего ${kpis.users_total}`}
        spark={{ values: kpis.active_users_14d_history, color: "var(--color-brand-2)" }}
        onClick={() => onOpen("users")}
      />
      <StatCard
        label={
          <>
            Средняя задержка
            <br />
            синхронизации
          </>
        }
        target={kpis.avg_sync_minutes}
        decimals={1}
        suffix=" мин"
        sub={`целевое значение ≤ ${kpis.sync_target_minutes.toFixed(0)} мин`}
        spark={{ values: kpis.avg_sync_history, color: delayColor }}
        onClick={() => onOpen("delay")}
      />
    </div>
  );
}

function StatCard({
  label,
  target,
  decimals,
  suffix = "",
  sub,
  spark,
  accent = false,
  onClick,
}: {
  label: React.ReactNode;
  target: number;
  decimals: number;
  suffix?: string;
  sub: string;
  spark: { values: number[]; color: string };
  accent?: boolean;
  onClick: () => void;
}) {
  const v = useCountUp(target, 900, decimals);
  return (
    <button
      onClick={onClick}
      className="group relative cursor-pointer rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] p-4 text-left transition-all duration-200 hover:-translate-y-0.5 hover:border-[var(--color-fg-muted)] hover:shadow-[0_12px_30px_-12px_rgba(15,23,42,0.18)]"
    >
      <div className="min-h-[26px] text-[10.5px] font-medium uppercase leading-snug tracking-[0.08em] text-[var(--color-fg-muted)]">
        {label}
      </div>
      <div
        className={
          "mt-2 text-[30px] font-semibold leading-none tracking-[-0.02em] " +
          (accent ? "text-[var(--color-brand)]" : "text-[var(--color-fg)]")
        }
      >
        {formatRu(v, decimals)}
        <span className="text-[20px] font-normal text-[var(--color-fg-faint)]">{suffix}</span>
      </div>
      <div className="mt-3 text-[11.5px] text-[var(--color-fg-muted)]">{sub}</div>
      <Sparkline values={spark.values} color={spark.color} className="mt-3 h-7 w-full" />
      <ArrowUpRight
        size={14}
        className="absolute right-3 top-3 text-[var(--color-fg-faint)] opacity-0 transition-opacity group-hover:opacity-100"
      />
    </button>
  );
}

// ───────────────────── Bottom: mesh + events ─────────────────────

function BottomGrid({
  locals,
  events,
  onNodeClick,
  onEventClick,
}: {
  locals: LocalServer[];
  events: LogEvent[];
  onNodeClick: (n: LocalServer) => void;
  onEventClick: (e: LogEvent) => void;
}) {
  return (
    <div className="mt-5 grid grid-cols-1 gap-3 lg:grid-cols-3">
      <div className="lg:col-span-2">
        <MeshCard locals={locals} onNodeClick={onNodeClick} />
      </div>
      <EventsCard events={events} onEventClick={onEventClick} />
    </div>
  );
}

function MeshCard({
  locals,
  onNodeClick,
}: {
  locals: LocalServer[];
  onNodeClick: (n: LocalServer) => void;
}) {
  return (
    <section className="rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] p-4">
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-[14px] font-semibold tracking-[-0.005em] text-[var(--color-fg)]">
            Меш локальных серверов
          </h2>
          <p className="mt-0.5 text-[11.5px] text-[var(--color-fg-muted)]">
            Связи между Глобальным и Локальными серверами — кликните по узлу
          </p>
        </div>
        <Legend
          items={[
            { color: "var(--color-success)", label: "онлайн" },
            { color: "var(--color-warning)", label: "устарел" },
            { color: "var(--color-danger)", label: "офлайн" },
          ]}
        />
      </div>
      <MeshDiagram locals={locals} onNodeClick={onNodeClick} />
    </section>
  );
}

function Legend({ items }: { items: { color: string; label: string }[] }) {
  return (
    <ul className="flex items-center gap-3 text-[11.5px] text-[var(--color-fg-muted)]">
      {items.map((it) => (
        <li key={it.label} className="flex items-center gap-1.5">
          <span className="inline-block h-2 w-2 rounded-full" style={{ background: it.color }} />
          {it.label}
        </li>
      ))}
    </ul>
  );
}

type MeshNodeLayout = { id: string; x: number; y: number };

const layout: MeshNodeLayout[] = [
  { id: "global", x: 50, y: 14 },
  { id: "spb-01", x: 50, y: 38 },
  { id: "msk-01", x: 18, y: 40 },
  { id: "msk-02", x: 32, y: 60 },
  { id: "ekb-01", x: 60, y: 60 },
  { id: "nsk-01", x: 88, y: 40 },
  { id: "kzn-01", x: 20, y: 80 },
  { id: "ros-01", x: 40, y: 86 },
  { id: "smr-01", x: 56, y: 86 },
  { id: "vld-01", x: 76, y: 80 },
];

const edges: [string, string][] = [
  ["global", "spb-01"],
  ["global", "msk-01"],
  ["global", "ekb-01"],
  ["global", "nsk-01"],
  ["spb-01", "msk-02"],
  ["msk-01", "msk-02"],
  ["msk-02", "kzn-01"],
  ["kzn-01", "ros-01"],
  ["ros-01", "smr-01"],
  ["smr-01", "ekb-01"],
  ["ekb-01", "vld-01"],
  ["smr-01", "vld-01"],
];

function MeshDiagram({
  locals,
  onNodeClick,
}: {
  locals: LocalServer[];
  onNodeClick: (n: LocalServer) => void;
}) {
  const W = 700, H = 320;
  const px = (n: number) => (n / 100) * W;
  const py = (n: number) => (n / 100) * H;
  const byId = useMemo(() => Object.fromEntries(layout.map((n) => [n.id, n])), []);
  const localById = useMemo(() => Object.fromEntries(locals.map((n) => [n.id, n])), [locals]);

  const statusFill = { global: "#1f3a8a", online: "#16a34a", stale: "#d97706", offline: "#dc2626" } as const;
  const offlineIds = new Set(locals.filter((s) => s.status === "offline").map((s) => s.id));

  return (
    <div className="mt-4">
      <svg viewBox={`0 0 ${W} ${H}`} className="block w-full">
        <defs>
          <pattern id="grid" width="14" height="14" patternUnits="userSpaceOnUse">
            <circle cx="1" cy="1" r="0.7" fill="#cbd5e1" opacity="0.55" />
          </pattern>
        </defs>
        <rect x="0" y="0" width={W} height={H} fill="url(#grid)" rx="6" />
        {edges.map(([a, b]) => {
          // Рисуем ребро ТОЛЬКО когда оба конца реально существуют в БД (или это GLOBAL).
          // Без этой проверки на пустой системе видны фейковые пунктиры в никуда.
          const aExists = a === "global" || !!localById[a];
          const bExists = b === "global" || !!localById[b];
          if (!aExists || !bExists) return null;
          const A = byId[a], B = byId[b];
          if (!A || !B) return null;
          const isDead = offlineIds.has(a) || offlineIds.has(b);
          return (
            <line
              key={`${a}-${b}`}
              x1={px(A.x)} y1={py(A.y)} x2={px(B.x)} y2={py(B.y)}
              stroke={isDead ? "#dc2626" : "#1f3a8a"}
              strokeOpacity={isDead ? 0.25 : 0.35}
              strokeWidth="1.2"
              strokeDasharray="4 4"
            />
          );
        })}
        {layout.map((n) => {
          if (n.id === "global") {
            const cx = px(n.x), cy = py(n.y);
            return (
              <g key="global">
                <circle cx={cx} cy={cy} r={14} fill={statusFill.global} />
                <circle cx={cx} cy={cy} r={20} fill="none" stroke={statusFill.global} strokeOpacity="0.18" strokeWidth="2" />
                <text x={cx} y={cy + 4} textAnchor="middle" fontSize="11" fill="#fff" fontWeight="700" fontFamily="IBM Plex Sans">G</text>
                <text x={cx} y={cy + 30} textAnchor="middle" fontSize="10" fill="#475569" fontFamily="IBM Plex Mono">GLOBAL</text>
              </g>
            );
          }
          const node = localById[n.id];
          if (!node) return null;
          const cx = px(n.x), cy = py(n.y);
          const stroke = statusFill[node.status as keyof typeof statusFill] ?? statusFill.offline;
          return (
            <g
              key={n.id}
              className="cursor-pointer transition-transform hover:scale-110"
              style={{ transformBox: "fill-box", transformOrigin: "center" }}
              onClick={() => onNodeClick(node)}
            >
              <circle cx={cx} cy={cy} r={14} fill="transparent" />
              <circle
                cx={cx} cy={cy} r={9}
                fill="#fff" stroke={stroke} strokeWidth={2.5}
                className={node.status === "offline" ? "node-pulse" : ""}
              />
              <text x={cx} y={cy + 22} textAnchor="middle" fontSize="10" fill="#475569" fontFamily="IBM Plex Mono">
                {n.id}
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}

function EventsCard({
  events,
  onEventClick,
}: {
  events: LogEvent[];
  onEventClick: (e: LogEvent) => void;
}) {
  return (
    <section className="rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] p-4">
      <div className="flex items-center justify-between">
        <h2 className="text-[14px] font-semibold tracking-[-0.005em] text-[var(--color-fg)]">
          Последние события
        </h2>
        <Link
          to="/admin/log"
          className="inline-flex items-center gap-1 text-[12px] text-[var(--color-fg-muted)] transition-colors hover:text-[var(--color-fg)]"
        >
          весь журнал
          <ArrowRight size={12} />
        </Link>
      </div>
      <ul className="mt-4 flex flex-col gap-1.5">
        {events.map((e, i) => (
          <li key={e.id} style={{ animationDelay: `${i * 60}ms` }} className="stagger-up">
            <button
              onClick={() => onEventClick(e)}
              className="group flex w-full items-start gap-3 rounded-lg px-2 py-2 text-left text-[12.5px] leading-snug transition-colors hover:bg-[var(--color-surface-2)]"
            >
              <span
                className="mt-1.5 inline-block h-2 w-2 shrink-0 rounded-full"
                style={{ background: dotColor(e.color) }}
              />
              <div className="flex-1 text-[var(--color-fg)]">
                <span className="font-mono text-[12px] font-medium">{e.who}</span>{" "}
                <span className="text-[var(--color-fg-muted)]">{e.text}</span>
              </div>
              <span className="shrink-0 text-[11px] text-[var(--color-fg-faint)]">{e.when}</span>
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function dotColor(c: EventColor) {
  switch (c) {
    case "green": return "var(--color-success)";
    case "red":   return "var(--color-danger)";
    case "blue":  return "var(--color-brand-2)";
    case "amber": return "var(--color-warning)";
    case "gray":  return "#94a3b8";
  }
}
